// src/components/BarcodeScannerModal.tsx - OPTIMIZED FOR CODE 39 / 1D BARCODES
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { Camera, CheckCircle, X, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export interface BarcodeScannerModalProps {
    isOpen: boolean
    onClose: () => void
    onScanSuccess: (code: string) => void
}

export default function BarcodeScannerModal({ isOpen, onClose, onScanSuccess }: BarcodeScannerModalProps) {
    const [scanning, setScanning] = useState(false);
    const [error, setError] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [detectedCode, setDetectedCode] = useState('');
    const [scanAttempts, setScanAttempts] = useState(0);

    const videoRef = useRef<HTMLVideoElement>(null);
    const readerRef = useRef<BrowserMultiFormatReader | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const hasDetectedRef = useRef(false); // Prevent multiple detections

    useEffect(() => {
        const checkMobile = () => {
            const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            setIsMobile(mobile);
        };
        checkMobile();
    }, []);

    useEffect(() => {
        if (isOpen) {
            startCamera();
        } else {
            // CRITICAL: Aggressively cleanup when modal closes
            console.log('🚫 Modal closed, forcing cleanup');
            stopCamera();
            setDetectedCode('');
            setError('');
            hasDetectedRef.current = true; // Prevent any pending callbacks
        }
        return () => {
            console.log('🧹 Component cleanup');
            stopCamera();
            hasDetectedRef.current = true; // Prevent any pending callbacks
        };
    }, [isOpen]);

    const startCamera = async () => {
        try {
            console.log('🎥 Starting camera...');

            // Stop any existing camera FIRST
            stopCamera();

            // Reset detection flag
            hasDetectedRef.current = false;

            // Then set states
            setError('');
            setScanning(true);
            setScanAttempts(0);
            setDetectedCode('');

            console.log('📱 Requesting camera access, isMobile:', isMobile);

            // Improved camera constraints for better barcode recognition
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: 'environment' },
                    width: { ideal: 1920, min: 1280 },
                    height: { ideal: 1080, min: 720 },
                    // Better focus for barcode scanning
                    aspectRatio: { ideal: 16 / 9 }
                }
            });

            console.log('✅ Camera stream obtained');
            streamRef.current = stream;

            if (videoRef.current) {
                console.log('📹 Setting video source...');
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
                console.log('▶️ Video playing');

                // Wait longer for camera to stabilize and focus
                setTimeout(() => {
                    console.log('🔍 Starting continuous scanning...');
                    startContinuousScanning();
                }, 1000); // Increased from 500ms to 1000ms
            } else {
                console.error('❌ Video ref is null');
                setError('Errore video element');
                setScanning(false);
            }
        } catch (err) {
            console.error('❌ Camera error:', err);
            setError(`Impossibile accedere alla camera: ${(err as Error).message}`);
            setScanning(false);
        }
    };

    const startContinuousScanning = () => {
        console.log('🔄 Setting up continuous scanning...');

        // Configure decode hints for optimal 1D barcode detection
        const hints = new Map();
        // TRY_HARDER: spend more time for accurate detection (critical for real-world photos)
        hints.set(DecodeHintType.TRY_HARDER, true);
        // ENABLE_CODE_39_EXTENDED_MODE: required for alphanumeric Code 39 (e.g. Renault "172028338R")
        hints.set(DecodeHintType.ENABLE_CODE_39_EXTENDED_MODE, true);
        // POSSIBLE_FORMATS: restrict to 1D barcode formats only (avoid wasting time on QR/2D)
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
            BarcodeFormat.CODE_39,
            BarcodeFormat.CODE_128,
            BarcodeFormat.EAN_13,
            BarcodeFormat.EAN_8,
            BarcodeFormat.ITF,
            BarcodeFormat.CODABAR,
            BarcodeFormat.UPC_A,
            BarcodeFormat.UPC_E,
        ]);

        const reader = new BrowserMultiFormatReader(hints);

        console.log('📋 Starting continuous decode with Code 39 Extended + TRY_HARDER hints');
        readerRef.current = reader;

        // Use ZXing's continuous decode from existing video element
        try {
            reader.decodeFromVideoElement(
                videoRef.current!,
                (result, error) => {
                    // CRITICAL: Check flag FIRST, before anything else
                    if (hasDetectedRef.current) {
                        // Already detected, ignore all future callbacks
                        console.log('🚫 Ignoring callback - already detected');
                        return;
                    }

                    setScanAttempts(prev => prev + 1);

                    if (result) {
                        console.log('✅ Barcode detected:', result.getText());
                        console.log('📊 Format:', result.getBarcodeFormat());

                        // Set flag IMMEDIATELY before doing anything else
                        hasDetectedRef.current = true;

                        // Stop video stream immediately - this stops the decoder
                        if (streamRef.current) {
                            streamRef.current.getTracks().forEach(track => track.stop());
                            streamRef.current = null;
                            console.log('🛑 Stream stopped immediately after detection');
                        }

                        // Now handle the success
                        handleScanSuccess(result.getText());
                        return; // Exit callback immediately
                    }

                    // Only log non-NotFoundException errors
                    if (error && error.name !== 'NotFoundException') {
                        console.log('⚠️ Decode error:', error.name, error.message);
                    }
                }
            );
            console.log('✅ Continuous scanning started (1D formats: CODE_39, CODE_128, EAN, UPC, ITF, CODABAR)');
        } catch (err) {
            console.error('❌ Failed to start continuous scanning:', err);
            setError('Errore avvio scansione');
        }
    };

    const stopCamera = () => {
        console.log('🛑 Stopping camera...');

        // Reset detection flag
        hasDetectedRef.current = false;

        // Just null out the reader ref - no reset method exists
        if (readerRef.current) {
            readerRef.current = null;
            console.log('✅ Reader nulled');
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
            console.log('✅ Stream stopped');
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setScanning(false);
        setScanAttempts(0);
    };

    const handleScanSuccess = (code: string) => {
        console.log('🎯 Scan success handler called for code:', code);

        // CRITICAL: Close modal FIRST to prevent any race conditions
        onClose();

        // Then trigger the success callback
        onScanSuccess(code);

        // UI feedback
        setDetectedCode(code);
        if (navigator.vibrate) {
            navigator.vibrate(200);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 overflow-hidden">
                {/* Header */}
                <div className="p-4 bg-blue-600 text-white flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        <h3 className="text-lg font-semibold">Scansiona Barcode</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-blue-700 rounded transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Success Indicator with Barcode Content */}
                {detectedCode && (
                    <div className="p-4 bg-green-50 border-b border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <p className="text-sm font-medium text-green-800">Codice rilevato!</p>
                        </div>
                        <div className="bg-white p-3 rounded border border-green-200">
                            <p className="text-xs text-slate-500 mb-1">Contenuto barcode:</p>
                            <p className="text-sm font-mono text-slate-800 break-all">{detectedCode}</p>
                        </div>
                    </div>
                )}

                {/* Error Display */}
                {error && (
                    <div className="p-4 bg-red-50 border-b border-red-200">
                        <p className="text-sm text-red-700 font-medium mb-1">Errore</p>
                        <p className="text-xs text-red-600">{error}</p>
                    </div>
                )}

                {/* Camera View */}
                <div className="p-4">
                    <div className="relative">
                        <video
                            ref={videoRef}
                            className="w-full rounded-lg bg-black"
                            style={{ maxHeight: '400px' }}
                            playsInline
                            muted
                            autoPlay
                        />

                        {scanning && !detectedCode && videoRef.current?.readyState === 4 && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="relative">
                                    {/* Larger scanning guide for desktop */}
                                    <div className={`border-4 rounded-lg animate-pulse ${isMobile ? 'w-64 h-48 border-blue-500' : 'w-96 h-64 border-green-500'
                                        }`}></div>
                                    <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded text-sm whitespace-nowrap">
                                        {isMobile ?
                                            `Scansione... (${scanAttempts})` :
                                            `Desktop Mode - Tentativi: ${scanAttempts}`
                                        }
                                    </div>
                                </div>
                            </div>
                        )}

                        {(!scanning || !videoRef.current || videoRef.current?.readyState !== 4) && !detectedCode && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                                <div className="text-center text-white">
                                    <Camera className="w-16 h-16 mx-auto mb-2" />
                                    <p>Inizializzazione...</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Help Text */}
                <div className="p-4 bg-slate-50 text-sm text-slate-600 border-t border-slate-200">
                    <p className="font-medium mb-2">💡 Suggerimenti:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Inquadra il barcode al centro del riquadro {isMobile ? 'blu' : 'verde'}</li>
                        <li>Mantieni la fotocamera stabile</li>
                        <li>Assicurati di avere buona illuminazione</li>
                        <li>Evita riflessi e ombre sul barcode</li>
                        {!isMobile && (
                            <>
                                <li className="text-green-700 font-medium">📸 Desktop: Avvicina il barcode lentamente alla camera</li>
                                <li className="text-green-700 font-medium">📸 Tieni il barcode parallelo allo schermo</li>
                                <li className="text-green-700 font-medium">📸 Distanza ideale: 15-25 cm dalla camera</li>
                            </>
                        )}
                    </ul>

                    {/* Debug Info */}
                    <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
                        <p>Debug: scanning={scanning ? 'true' : 'false'}, mobile={isMobile ? 'true' : 'false'}</p>
                        <p className="mt-1">Modalità: {isMobile ? '📱 Mobile' : '💻 Desktop'} (Environment Camera)</p>
                        <p className="mt-1">Formati supportati: CODE-39 (extended), CODE-128, EAN-13, EAN-8, UPC, ITF, CODABAR</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
